import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { PropertyList } from '@/components/properties/property-list';
import { PropertyForm } from '@/components/properties/property-form';
import { PropertyDetails } from '@/components/properties/property-details';
import { useToast } from '@/hooks/use-toast';
// @ts-ignore - TODO: Fix this TypeScript error
import type { Property } from '@/lib/types';

// Mock data
const mockProperties: Property[] = [
  {
    id: '1',
    name: 'Sunset Apartments',
    address: '123 Sunset Blvd, Los Angeles, CA 90028',
    type: 'residential',
    size: 25000,
    units: 50,
    mainContact: {
      name: 'John Smith',
      phone: '(555) 123-4567',
      email: 'john@example.com',
    },
    status: 'active',
    lastService: '2024-01-10',
  },
  {
    id: '2',
    name: 'Oak Residences',
    address: '456 Oak Street, Los Angeles, CA 90012',
    type: 'residential',
    size: 18000,
    units: 35,
    mainContact: {
      name: 'Sarah Johnson',
      phone: '(555) 234-5678',
      email: 'sarah@example.com',
    },
    status: 'active',
    lastService: '2024-01-12',
  },
];

type View = 'list' | 'form' | 'details';

export function PropertiesPage() {
  const [view, setView] = useState<View>('list');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const { toast } = useToast();

  const handleCreateProperty = async (data: Partial<Property>) => {
    try {
      // Here you would make an API call to create the property
      console.log('Creating property:', data);
      
      toast({
        title: 'Success',
        description: 'Property created successfully',
      });
      setView('list');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create property',
      });
    }
  };

  const handleUpdateProperty = async (data: Partial<Property>) => {
    try {
      // Here you would make an API call to update the property
      console.log('Updating property:', data);
      
      toast({
        title: 'Success',
        description: 'Property updated successfully',
      });
      setView('list');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update property',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          {view === 'list' && 'Properties'}
          {view === 'form' && (selectedProperty ? 'Edit Property' : 'Add Property')}
          {view === 'details' && 'Property Details'}
        </h1>
        <div className="flex gap-2">
          {view !== 'list' && (
            <Button
              variant="outline"
              onClick={() => {
                setView('list');
                setSelectedProperty(null);
              }}
            >
              Back to List
            </Button>
          )}
          {view === 'list' && (
            <Button onClick={() => setView('form')}>
              <Plus className="mr-2 h-4 w-4" />
              Add Property
            </Button>
          )}
        </div>
      </div>

      {view === 'list' && (
        <PropertyList
          properties={mockProperties}
          onPropertyClick={(property) => {
            setSelectedProperty(property);
            setView('details');
          }}
        />
      )}

      {view === 'form' && (
        <PropertyForm
          property={selectedProperty}
          onSubmit={selectedProperty ? handleUpdateProperty : handleCreateProperty}
        />
      )}

      {view === 'details' && selectedProperty && (
        <PropertyDetails
          property={selectedProperty}
          onEdit={() => setView('form')}
        />
      )}
    </div>
  );
}